<template>
  <v-card class="elevation-0">

    <v-select
      v-model="selectedFacilityNames"
      :items="facilityNames"
      :multiple="multiple"
      :disabled="disabled"
      :color="selectColor"
      readonly
      chips
      class="mb-3 mr-2"
      hide-details="auto"
      ref="input"
      >
      <template v-slot:label>
        <span :class="selectLabelClass">{{ label }}</span>
      </template>

      <template v-slot:append>
        <v-icon
          large
          @click="pickFacilities()"
          >domain
        </v-icon>
      </template>

      <template v-slot:selection="{ item }">
        <v-chip
          dark
          close
          close-icon="mdi-close-outline"
          :color="chipColor"
          @click:close="removeFacility(item)"
          >
          <span class="text-truncate">{{ item }}</span>
        </v-chip>
      </template>
    </v-select>
    <p v-if="error" class="red--text">{{ error }}</p>

    <v-dialog v-model="dialog" max-width="500px" :persistent="true">
      <loading
        :active.sync="isLoadingOrSaving"
        :can-cancel="false"
        loader="dots"
        :color="appColor_Progress"
      />
      <template v-if="!isOptions">
        <v-card>
          <v-toolbar dark flat dense :color="toolbarColor" class="mb-3">
            <v-btn fab small @click="cancel" class="elevation-0" :color="toolbarColorDark">
              <v-icon>close</v-icon>
            </v-btn>
          </v-toolbar>
          <v-card-text>
            <v-row justify="center">
              <h2 class="font-weight-light">
                {{ getExpression('Alerts', 'NoFacilitiesFound') }}
              </h2>
            </v-row>
          </v-card-text>
        </v-card>
      </template>

      <template v-else>

        <!-- Header Toolbar -->
        <v-toolbar dark flat dense :color="toolbarColor">

          <!-- Cancel -->
          <v-btn fab small @click="cancel" class="elevation-0" :color="toolbarColorDark">
            <v-icon>close</v-icon>
          </v-btn>

          <v-toolbar-title><v-icon class="mx-2">domain</v-icon>{{ title }}</v-toolbar-title>

          <v-spacer />
          
          <!-- OK -->
          <v-toolbar-items>
            <v-btn
              :disabled="selected.length === 0 || isSaving"
              :loading="isSaving"
              dark
              :color="toolbarColorDark"
              @click="select"
              class="elevation-0"
              >
              {{ getExpression('Button', 'OK') }}
            </v-btn>
          </v-toolbar-items>
        </v-toolbar>

        <!-- Data Table -->
        <v-data-table
          v-model="selected"
          :headers="headers"
          :items="options"
          :loading="isLoading"
          :search="search"
          :sort-by.sync="sortBy"
          :sort-desc.sync="sortDesc"
          :page.sync="page"
          :footer-props="{'items-per-page-options': [10]}"
          show-select
          :single-select="!multiple"
          item-key="id"
          >
          <v-progress-linear slot="progress" :color="appColor_Progress" indeterminate />

          <template v-slot:top>
            <v-row no-gutters class="mx-3">

              <!-- Search -->
              <v-col cols="12" md="12">
                <v-text-field
                  v-model="search"
                  append-icon="search"
                  label="Search"
                  single-line
                  hide-details
                  :disabled="isLoading"
                  clearable
                  class="ml-3"
                  >
                </v-text-field>
              </v-col>
            </v-row>
          </template>
        </v-data-table>
      </template>
    </v-dialog>
  </v-card>
</template>

<script>
import { AppColor } from '@/config.js'

export default {
  name: 'FacilitySelector',
  emit: ['input'],
  $_veeValidate: {
    name () {
      return this.label
    },
    value () {
      return this.selectedFacilityNames.length > 0
        ? this.selectedFacilityNames
        : null
    }
  },
  emits: ['input'],
  props: {
    options: {
      type: Array,
      default: null
    },
    value: {
      type: Array,
      default: null
    },
    label: {
      type: String
    },
    multiple: {
      type: Boolean,
      default: false
    },
    close: {
      type: Boolean,
      default: true
    },
    disabled: {
      type: Boolean,
      default: false
    },
    chipColor: {
      type: String,
      default: AppColor.Medium
    },
    error: {
      type: String,
      default: null
    },
    toolbarColor: {
      type: String,
      default: AppColor.Medium
    },
    toolbarColorDark: {
      type: String,
      default: AppColor.Dark
    }
  },
  data() {
    return {
      appColor_Medium: AppColor.Medium,
      appColor_Progress: AppColor.Progress,
      
      isLoading: true,
      isSaving: false,
      
      page: 1,
      selected: [],
      search: '',
      sortBy: 'name',
      sortDesc: false,

      // options table
      headers: [
        {
          text: 'Name',
          value: 'name',
          align: 'left',
          width: '60%'
        },
        {
          text: 'Location',
          value: 'geographicalDescription',
          align: 'left',
          width: '40%'
        }
      ],

      dialog: false,
      selectedFacilityIds: []
    }
  },
  computed: {
    title () {
      return this.multiple
        ? 'Select Facilities'
        : 'Select Facility'
    },
    facilityNames () {
      return this.options !== null
        ? this.options.map(o => o.name)
        : []
    },
    selectedIds () {
      return this.value ?? []
    },
    selectedFacilities () {
      const ids = this.selectedIds
      return this.options.filter(i => ids.includes(i.id))
    },
    selectedFacilityNames () {
      return this.multiple
        ? this.selectedFacilities.map(f => f.name)
        : this.selectedFacilities.length > 0
          ? this.selectedFacilities[0].name
          : null
    },
    isLoadingOrSaving () {
      return this.isLoading || this.isSaving
    },
    isOptions () {
      return this.options.length > 0
    },
    selectColor () {
      return this.error
        ? 'red'
        : ''
    },
    selectLabelClass () {
      return this.error
        ? 'red--text'
        : ''
    }
  },
  methods: {
    loadData () {
      this.isLoading = true
      if (this.selectedIds !== null) {
        this.selected = this.options.filter(f => {
          return this.selectedIds.includes(f.id) 
        })
      }
      this.isLoading = false
    },

    pickFacilities () {
      const value = this.value
      this.selected = this.options.filter(o => value?.includes(o.id))
      this.loadData()
      this.dialog = true
    },

    select () {
      this.selectedFacilityIds = this.selected.map(s => s.id)
      this.dialog = false
      this.emitValue()
    },

    cancel () {
      this.dialog = false
      this.reset()
    },

    focus () {
      this.$refs.input.focus()
    },

    reset () {
      const self = this
      setTimeout(() => {
        self.search = ''
        self.isLoading = false
        self.isSaving = false
        self.sortBy = 'name'
        self.sortDesc = false
        self.page = 1
      }, 300)
    },

    removeFacility (name) {
      const self = this

      // initialize these arrays when item is removed before any interaction with the datatable
      if (self.selected.length === 0) {
        self.selectedFacilities.forEach(f => self.selected.push(f))
      }

      if (self.selectedFacilityIds.length === 0) {
        self.selected.forEach(f => self.selectedFacilityIds.push(f.id))
      }

      // standard remove logic
      var id = -1
      const idx1 = self.selected.findIndex(f => f.name === name)
      if (idx1 >= 0) {
        id = self.selected[idx1].id
        self.selected.splice(idx1, 1)
      }

      const idx2 = self.selectedFacilityIds.findIndex(sid => sid === id)
      if (idx2 >= 0) {
        self.selectedFacilityIds.splice(idx2, 1)
      }

      self.emitValue()
    },

    emitValue () {
      // emit the bound valud
      const value = this.selectedFacilityIds.length > 0
        ? this.selectedFacilityIds
        : null
      this.$emit('input', value)
    },

    handleError (result) {
      this.$error.show({
        title: result.errorSeverity === 0
          ? this.getExpression('FormTitle', 'ValidationFailed')
          : this.getExpression('FormTitle', 'InternalError'),
        text: result.errorMessage,
        severity: result.errorSeverity
      })
    }
  },
  watch: {
    selectedFacilityIds () {
      this.emitValue()
    }
  }
}
</script>
